/**
 * Field definitions for the Entries grid
 * Extends base field definitions with entry-specific configurations
 */

import React from 'react';
import { Typography, Chip } from '@mui/material';
import { BASE_FIELDS } from '@/shared/config/baseFieldDefinitions';
import { createColumnDef } from '@/shared/config/fieldUtils.jsx';

/**
 * Entry-specific fields for spec columns
 */
const FMO_INPUT_SPEC_FIELDS = {
  cutoff: {
    field: 'cutoff',
    headerName: 'Cutoff',
    width: (theme) => theme ? parseInt(theme.spacing(10)) : 80,
    type: 'text',
    sortable: true,
    defaultVisible: true,
    valueGetter: (value, row) => row.clusterspec?.name || '-'
  },
  
  capping: {
    field: 'capping',
    headerName: 'Capping',
    width: (theme) => theme ? parseInt(theme.spacing(16)) : 128,
    type: 'text',
    sortable: true,
    defaultVisible: true,
    valueGetter: (value, row) => row.pocketspec?.name || '-'
  },
  
  config: {
    field: 'config',
    headerName: 'Config',
    width: (theme) => theme ? parseInt(theme.spacing(14)) : 112,
    type: 'text',
    sortable: true,
    defaultVisible: true,
    valueGetter: (value, row) => row.inputspec?.name || '-'
  }
};

/**
 * Get entry grid columns with proper order and configuration
 */
export const getFMOInputColumns = (theme, context = {}) => {
  return [
    // ID column
    createColumnDef(BASE_FIELDS.id_, {}, theme),
    
    // Source path
    createColumnDef(BASE_FIELDS.source_path, {
      flex: 1,
      valueGetter: (value, row) => {
        // For entries, use prep source_path if available
        return row.prep?.source_path || row.source_path || `entry_${row.id_}`;
      }
    }, theme),
    
    // Spec columns (Cutoff, Capping, Config)
    createColumnDef(FMO_INPUT_SPEC_FIELDS.cutoff, {}, theme),
    createColumnDef(FMO_INPUT_SPEC_FIELDS.capping, {}, theme),
    createColumnDef(FMO_INPUT_SPEC_FIELDS.config, {}, theme),
    
    // Ligand fields from prep (now visible by default)
    createColumnDef(BASE_FIELDS.ligand_name, {
      field: 'ligandName',
      valueGetter: (value, row) => row.prep?.ligand_name || '-',
      defaultVisible: true  // Now visible by default
    }, theme),
    
    createColumnDef(BASE_FIELDS.ligand_tag, {
      field: 'ligandTag', 
      valueGetter: (value, row) => row.prep?.ligand_tag || '-',
      defaultVisible: true  // Now visible by default
    }, theme),
    
    // Project hierarchy from prep (hidden by default)
    createColumnDef(BASE_FIELDS.project, {
      valueGetter: (value, row) => row.prep?.project || '-',
      defaultVisible: false
    }, theme),
    
    createColumnDef(BASE_FIELDS.target, {
      valueGetter: (value, row) => row.prep?.target || '-',
      defaultVisible: false
    }, theme),
    
    createColumnDef(BASE_FIELDS.batch, {
      valueGetter: (value, row) => row.prep?.batch || '-',
      defaultVisible: false
    }, theme),
    
    // Mutation (hidden by default)
    createColumnDef(BASE_FIELDS.mutation, {
      valueGetter: (value, row) => row.prep?.mutation || '-',
      defaultVisible: false
    }, theme),
  ];
};

/**
 * Get default column visibility for entries grid
 */
export const getFMOInputDefaultVisibility = (columns) => {
  return columns.reduce((acc, col) => {
    if (col.defaultVisible === false) {
      acc[col.field] = false;
    }
    return acc;
  }, {});
};